import React, { useEffect, useState, useMemo } from "react";
import { PageHero, FadeUp } from "../../components/public/Section";
import { api } from "../../lib/api";
import { Search, User, Building2, MapPin, ChevronLeft, ChevronRight } from "lucide-react";

const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const PAGE_SIZE = 12;

export default function Members() {
  const [q, setQ] = useState("");
  const [letter, setLetter] = useState("");
  const [specialization, setSpecialization] = useState("");
  const [hospital, setHospital] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0, specializations: [], hospitals: [] });

  useEffect(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (letter) params.set("letter", letter);
    if (specialization) params.set("specialization", specialization);
    if (hospital) params.set("hospital", hospital);
    params.set("limit", PAGE_SIZE);
    params.set("skip", page * PAGE_SIZE);
    api.get(`/members?${params}`).then((r) => setData(r.data));
  }, [q, letter, specialization, hospital, page]);

  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div>
      <PageHero title="Members Directory" subtitle="Search and connect with ophthalmologists from across our society." breadcrumbs={["Home", "Members", "Directory"]} />
      <section className="section container-page">
        {/* Filters */}
        <div className="card-elev p-5 md:p-6 mb-8 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Search members by name or hospital…" className="form-input pl-9" data-testid="members-search" />
          </div>
          <select value={specialization} onChange={(e) => { setSpecialization(e.target.value); setPage(0); }} className="form-input" data-testid="members-spec">
            <option value="">All Specializations</option>
            {data.specializations.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select value={hospital} onChange={(e) => { setHospital(e.target.value); setPage(0); }} className="form-input" data-testid="members-hospital">
            <option value="">All Hospitals</option>
            {data.hospitals.map((h) => <option key={h}>{h}</option>)}
          </select>
        </div>

        {/* Alphabet filter */}
        <div className="flex flex-wrap gap-1.5 mb-8">
          <button onClick={() => { setLetter(""); setPage(0); }} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${!letter ? "bg-brand-primary text-white" : "bg-white border border-brand-border text-brand-text hover:bg-brand-primary-light"}`}>All</button>
          {ALPHA.map((l) => (
            <button key={l} onClick={() => { setLetter(l); setPage(0); }} className={`w-9 h-9 rounded-md text-sm font-semibold transition-colors ${letter === l ? "bg-brand-primary text-white" : "bg-white border border-brand-border text-brand-text hover:bg-brand-primary-light"}`}>{l}</button>
          ))}
        </div>

        {/* Members grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.items.map((m) => (
            <FadeUp key={m.id} className="card-elev p-5 flex gap-4">
              <div className="w-16 h-16 rounded-full bg-brand-primary-light text-brand-primary flex items-center justify-center shrink-0"><User size={24} /></div>
              <div className="min-w-0">
                <div className="font-serif font-bold text-brand-text">{m.name}</div>
                <div className="text-xs text-brand-primary font-semibold uppercase tracking-wider mt-0.5">{m.specialization}</div>
                <div className="text-sm text-brand-muted mt-2 flex items-center gap-1.5"><Building2 size={14} /> {m.hospital}</div>
                <div className="text-sm text-brand-muted flex items-center gap-1.5 mt-1"><MapPin size={14} /> {m.city}</div>
                {m.membership_no && <div className="text-[11px] text-brand-muted mt-2">Membership: {m.membership_no}</div>}
              </div>
            </FadeUp>
          ))}
          {data.items.length === 0 && <div className="col-span-full text-center text-brand-muted py-14">No members match the current filters.</div>}
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            <button disabled={page === 0} onClick={() => setPage(page - 1)} className="btn-ghost disabled:opacity-40"><ChevronLeft size={16} /> Prev</button>
            <div className="text-sm text-brand-muted">Page {page + 1} of {pages}</div>
            <button disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} className="btn-ghost disabled:opacity-40">Next <ChevronRight size={16} /></button>
          </div>
        )}
      </section>
    </div>
  );
}
